import React from "react";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa6";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaClock,
  FaLocationArrow,
} from "react-icons/fa";
import guitarImg from "../images/gallery/guitars.webp";

const ADDRESS =
  "1st Floor, Plot No: 14A, Hi-Tension Rd, Eshwaripuri Colony, Kapra, Hyderabad, Telangana 500062";
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  ADDRESS
)}`;

const details = [
  { icon: FaMapMarkerAlt, title: "Address", lines: [ADDRESS] },
  { icon: FaPhoneAlt, title: "Call Us", lines: ["+91 99669 08986"] },
  {
    icon: FaClock,
    title: "Class Timings",
    lines: ["Tuesday – Sunday: 3 PM – 8 PM", "Monday: Closed"],
  },
];

const listVariant = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const ContactPage = () => {
  const sendMessage = (e) => {
    e.preventDefault();
    const defaultMessage = encodeURIComponent(
      "Hello, I'd like to know more about the music class."
    );
    window.open(`https://wa.me/919966908986?text=${defaultMessage}`, "_blank");
  };

  return (
    <section className="bg-white">
      {/* Header */}
      <div className="mx-auto max-w-6xl px-6 pb-12 pt-20 text-center md:px-12 md:pt-24">
        <motion.span
          className="eyebrow mb-5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Get In Touch
        </motion.span>
        <motion.h1
          className="section-heading"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Let's Make Music Together
        </motion.h1>
        <motion.p
          className="mx-auto mt-4 max-w-2xl text-ink-soft"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Have a question or ready to book your free trial? Reach out — we'd
          love to hear from you.
        </motion.p>
      </div>

      {/* Content grid */}
      <div className="mx-auto max-w-6xl px-6 md:px-12">
        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-5">
          {/* Image */}
          <motion.div
            className="relative min-h-[22rem] overflow-hidden rounded-3xl shadow-card lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <img
              src={guitarImg}
              alt="Guitars at ABC School of Music"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <p className="font-display text-xl font-bold text-white">
                ABC School of Music
              </p>
              <p className="mt-1 flex items-start gap-2 text-sm text-gray-200">
                <FaMapMarkerAlt className="mt-1 flex-none text-brand-300" />
                Kapra, Hyderabad
              </p>
            </div>
          </motion.div>

          {/* Details card */}
          <motion.div
            className="rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-100 sm:p-10 lg:col-span-3"
            variants={listVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h2
              className="font-display text-2xl font-bold text-ink md:text-3xl"
              variants={itemVariant}
            >
              Visit or reach out
            </motion.h2>
            <motion.p
              className="mt-2 text-sm text-ink-soft"
              variants={itemVariant}
            >
              We're happy to answer any questions about classes, exams, or
              schedules.
            </motion.p>

            <div className="mt-8 space-y-6">
              {details.map(({ icon: Icon, title, lines }) => (
                <motion.div
                  key={title}
                  className="group flex gap-4"
                  variants={itemVariant}
                >
                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white text-ink shadow-sm ring-1 ring-gray-100 transition-colors duration-300 group-hover:bg-brand group-hover:text-white group-hover:ring-brand">
                    <Icon />
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{title}</p>
                    {lines.map((line) => (
                      <p key={line} className="text-sm text-ink-soft">
                        {line}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Actions */}
            <motion.div
              className="mt-8 flex flex-col gap-3 border-t border-gray-200 pt-8 sm:flex-row"
              variants={itemVariant}
            >
              <motion.a
                href="#"
                onClick={sendMessage}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaWhatsapp className="text-2xl" />
                <span>Chat on WhatsApp</span>
              </motion.a>
              <motion.a
                href="tel:+919966908986"
                className="btn-primary flex-1"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaPhoneAlt className="text-sm" />
                <span>Call Now</span>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Dark map */}
      <div className="mx-auto max-w-6xl px-6 pb-24 pt-16 md:px-12">
        <motion.div
          className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <span className="eyebrow mb-3">Find Us</span>
            <h2 className="font-display text-2xl font-bold text-ink md:text-3xl">
              On the map
            </h2>
          </div>
          <a
            href={MAPS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            <FaLocationArrow className="text-sm text-brand" />
            Get Directions
          </a>
        </motion.div>

        <motion.div
          className="relative overflow-hidden rounded-3xl shadow-card ring-1 ring-ink/10"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="ABC School of Music location"
            src={`https://www.google.com/maps?q=${encodeURIComponent(
              ADDRESS
            )}&z=16&output=embed`}
            className="h-80 w-full border-0 md:h-[26rem]"
            style={{
              filter:
                "invert(92%) hue-rotate(180deg) brightness(0.9) contrast(0.95) saturate(0.8)",
            }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          {/* subtle inner ring to blend the dark map edges */}
          <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />
        </motion.div>
      </div>
    </section>
  );
};

export default ContactPage;
